import {filterObject} from '../util/util';
import {Evented} from '../util/evented';
import {PropertyValues, PossiblyEvaluated, PossiblyEvaluatedPropertyValue, ZOOM_DEPENDENCY, LIGHT_DEPENDENCY, IMAGE_DEPENDENCY} from './properties';
import {supportsPropertyExpression} from '../style-spec/util/properties';
import featureFilter from '../style-spec/feature_filter/index';
import {makeFQID} from '../util/fqid';
import {createRuntimeLayerUID} from '../placement/layer_uid';
import {type FeatureState} from '../style-spec/expression/index';
import {isStateConstant} from '../style-spec/expression/is_constant';
import assert from '../style-spec/util/assert';
import SymbolAppearance from './appearance';

import type {Bucket, BucketParameters} from '../data/bucket';
import type Point from '@mapbox/point-geometry';
import type {FeatureFilter, FilterExpression} from '../style-spec/feature_filter/index';
import type {TransitionParameters, PropertyValue, ConfigOptions, Properties} from './properties';
import type EvaluationParameters from './evaluation_parameters';
import type Transform from '../geo/transform';
import type {
    LayerSpecification,
    LayoutSpecification,
    PaintSpecification,
    FilterSpecification,
    PropertyValueSpecification,
    AppearanceSpecification
} from '../style-spec/types';
import type {CustomLayerInterface} from './style_layer/custom_style_layer';
import type {Map as MapboxMap} from '../ui/map';
import type {QueryGeometry, TilespaceQueryGeometry} from './query_geometry';
import type {DEMSampler} from '../terrain/elevation';
import type {VectorTileFeature} from '@mapbox/vector-tile';
import type {CreateProgramParams} from '../render/painter';
import type SourceCache from '../source/source_cache';
import type Tile from '../source/tile';
import type {SymbolPlacementParameters} from '../placement/symbol_placement_parameters';
import type {TileCoverageRect} from '../placement/types';
import type Painter from '../render/painter';
import type {LUT} from '../util/lut';
import type {ImageId} from '../style-spec/expression/types/image_id';
import type {ProgramName} from '../render/program';
import type {QueryResult} from '../source/query_features';

const TRANSITION_SUFFIX = '-transition';

export type RuntimeModuleType = 'HD' | 'Standard' | 'Lite' | 'RasterArray';

type LayerRenderingStats = {
    numRenderedVerticesInTransparentPass: number;
    numRenderedVerticesInShadowPass: number;
};

// Symbols are draped only on native and for certain cases only
const drapedLayers = new Set(['fill', 'line', 'background', 'hillshade', 'raster']);

class StyleLayer extends Evented {
    id: string;
    fqid: string;
    readonly runtimeLayerUID: number;
    scope: string;
    lut: LUT | null;
    metadata: unknown;
    type: LayerSpecification['type'] | 'custom';
    source!: string;
    sourceFqid: string | undefined;
    sourceLayer: string | null | undefined;
    slot: string | null | undefined;
    minzoom: number | null | undefined;
    maxzoom: number | null | undefined;
    filter: FilterSpecification | undefined;
    visibility: 'visible' | 'none' | undefined;
    iconImageUseTheme: string | null | undefined;
    appearances: Array<SymbolAppearance>;
    appearancesVersion: number;
    // Bumped whenever evaluated paint values may change without a relayout: on a paint property change and on
    // every frame of a paint transition, including the last one.
    paintVersion: number;
    // Whether the layer must be re-evaluated even if no input in its property dependencies changed: set on a value
    // or transition change and kept while a transition is in progress.
    _dirty: boolean;
    // The parameters and images of the last re-evaluation, to tell which property dependencies have changed
    _recalcParameters: EvaluationParameters | null;
    _recalcImages: ImageId[] | null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _layoutValues!: PropertyValues<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly layout!: PossiblyEvaluated<any>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _paintValues!: PropertyValues<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly paint!: PossiblyEvaluated<any>;

    _featureFilter: FeatureFilter;
    _filterCompiled: boolean;

    options: ConfigOptions | null | undefined;
    _stats: LayerRenderingStats | null | undefined;

    constructor(
        layer: LayerSpecification | CustomLayerInterface,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties: Readonly<{layout?: Properties<any>; paint?: Properties<any>;}>,
        scope: string,
        lut: LUT | null,
        options?: ConfigOptions | null,
        iconImageUseTheme?: string | null
    ) {
        super();

        this.id = layer.id;
        this.fqid = makeFQID(this.id, scope);
        this.runtimeLayerUID = createRuntimeLayerUID();
        this.type = layer.type;
        this.scope = scope;
        this.lut = lut;
        this.options = options;
        this.iconImageUseTheme = iconImageUseTheme;
        this.appearances = new Array<SymbolAppearance>();
        this.appearancesVersion = 0;
        this.paintVersion = 0;
        this._dirty = true;
        this._recalcParameters = null;
        this._recalcImages = null;

        this._featureFilter = {filter: () => true, needGeometry: false, needFeature: false};
        this._filterCompiled = false;

        if (layer.type === 'custom') return;

        this.metadata = layer.metadata;
        this.minzoom = layer.minzoom;
        this.maxzoom = layer.maxzoom;

        if (layer.type && layer.type !== 'background' && layer.type !== 'sky' && layer.type !== 'slot' && layer.type !== 'placement-group') {
            this.source = layer.source;
            this.sourceFqid = makeFQID(layer.source, scope);
            this.sourceLayer = layer['source-layer'];
            this.filter = layer.filter;
        }

        if (layer.slot) this.slot = layer.slot;

        if (layer.appearances) {
            this.setAppearances(layer.appearances);
        }

        if (properties.layout) {
            this._layoutValues = new PropertyValues(properties.layout, this.scope, options, this.iconImageUseTheme);
        }

        if (properties.paint) {
            this._paintValues = new PropertyValues(properties.paint, this.scope, options);

            for (const property in layer.paint) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.setPaintProperty(property as keyof PaintSpecification, layer.paint[property]);
            }
            for (const property in layer.layout) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.setLayoutProperty(property, layer.layout[property]);
            }
            this._paintValues.snap();
            this.paint = new PossiblyEvaluated(properties.paint);
        }
    }

    // No-op in the StyleLayer class, must be implemented by each concrete StyleLayer
    onAdd(_map: MapboxMap): void {}

    // No-op in the StyleLayer class, must be implemented by each concrete StyleLayer
    onRemove(_map: MapboxMap): void {}

    // Feeds this layer's placeable symbols into the given global placement run. No-op for
    // non-symbol layers; overridden by SymbolStyleLayer.
    placeSymbols(_parameters: SymbolPlacementParameters, _tiles: Array<Tile>, _styleLayerOrder: number, _sourceCache: SourceCache, _checkAgainstClipLayer: boolean, _childCoverageRectsByTileKey: ReadonlyMap<number, Array<TileCoverageRect>>): void {}

    isDraped(_sourceCache?: SourceCache): boolean {
        return !this.is3D(true) && drapedLayers.has(this.type);
    }

    getLayoutProperty<T extends keyof LayoutSpecification>(name: T): LayoutSpecification[T] | undefined {
        if (name === 'visibility') {
            return this.visibility as LayoutSpecification[T];
        }

        return this._layoutValues.getValue(name) as LayoutSpecification[T] | undefined;
    }

    setLayoutProperty<T extends keyof LayoutSpecification>(name: string, value: LayoutSpecification[T]): void {
        if (this.type === 'custom' && name === 'visibility') {
            this.visibility = value as 'visible' | 'none' | undefined;
            return;
        }

        const layout = this._layoutValues;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const specProps = layout._properties.properties;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!specProps[name]) return; // skip unrecognized properties

        layout.setValue(name, value);
        this._dirty = true;
        if (name === 'visibility') {
            this.possiblyEvaluateVisibility();
        }
    }

    setAppearances(appearances: AppearanceSpecification[]) {
        this.appearances = [];
        appearances.forEach(a => {
            this.appearances.push(new SymbolAppearance(a.condition, a.name, a.properties, this.scope, this.options, this.iconImageUseTheme));
        });
        this.appearancesVersion++;
        this._dirty = true;
    }

    possiblyEvaluateVisibility() {
        const value = this.getLayoutPropertyValue('visibility');
        if (value) this.visibility = value.possiblyEvaluate({zoom: 0} as EvaluationParameters) as 'visible' | 'none' | undefined;
    }

    // The `PropertyValue` set for a layout or paint property or its default; undefined if the layer type lacks it
    getLayoutPropertyValue<T = unknown, R = unknown>(name: string): PropertyValue<T, R> | undefined {
        const entry = this._layoutValues && this._layoutValues._values[name];
        return (entry && entry.value) as PropertyValue<T, R> | undefined;
    }

    getPaintPropertyValue<T = unknown, R = unknown>(name: string): PropertyValue<T, R> | undefined {
        const entry = this._paintValues && this._paintValues._values[name];
        return (entry && entry.value) as PropertyValue<T, R> | undefined;
    }

    getPaintProperty<T extends keyof PaintSpecification>(name: T): PaintSpecification[T] | undefined {
        if (name.endsWith(TRANSITION_SUFFIX)) {
            return this._paintValues.getTransition(name.slice(0, -TRANSITION_SUFFIX.length)) as PaintSpecification[T];
        } else {
            return this._paintValues.getValue(name) as PaintSpecification[T];
        }
    }

    isPaintProperty<T extends keyof PaintSpecification>(name: T): boolean {
        const paint = this._paintValues;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const specProps = paint._properties.properties;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return !!specProps[name];
    }

    setPaintProperty<T extends keyof PaintSpecification>(name: T, value: PaintSpecification[T]): boolean {
        const paint = this._paintValues;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const specProps = paint._properties.properties;

        if (name.endsWith(TRANSITION_SUFFIX)) {
            const propName = name.slice(0, -TRANSITION_SUFFIX.length);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (specProps[propName]) { // skip unrecognized properties
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
                paint.setTransition(propName, (value as any) || undefined);
            }
            return false;

        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!specProps[name]) return false; // skip unrecognized properties

        const transitionable = paint._values[name];
        const wasDataDriven = transitionable.value.isDataDriven();
        const oldValue = transitionable.value;

        paint.setValue(name, value as PropertyValueSpecification<unknown>);
        this._handleSpecialPaintPropertyUpdate(name);

        const newValue = paint._values[name].value;
        const isDataDriven = newValue.isDataDriven();
        const isPattern = name.endsWith('pattern') || name === 'line-dasharray';

        // if a pattern value is changed, we need to make sure the new icons get added to each tile's iconAtlas
        // so a call to _updateLayer is necessary, and we return true from this function so it gets called in
        // Style#setPaintProperty
        return isDataDriven || wasDataDriven || isPattern || this._handleOverridablePaintPropertyUpdate(name, oldValue, newValue);
    }

    _handleSpecialPaintPropertyUpdate(_: string) {
        // No-op; can be overridden by derived classes.
    }

    getProgramIds(): ProgramName[] | null {
        // No-op; can be overridden by derived classes.
        return null;
    }

    getDefaultProgramParams(name: string, zoom: number, lut: LUT | null): CreateProgramParams | null {
        // No-op; can be overridden by derived classes.
        return null;
    }

    _handleOverridablePaintPropertyUpdate<T, R>(name: string, oldValue: PropertyValue<T, R>, newValue: PropertyValue<T, R>): boolean {
        // No-op; can be overridden by derived classes.
        return false;
    }

    /**
     * Returns true if the paint change since `prevPaint` invalidates the last label placement.
     * @private
     */
    isPlacementAffectedByPaintChanges(prevPaint: unknown): boolean {
        // No-op; can be overridden by derived classes.
        return false;
    }

    isHidden(zoom: number): boolean {
        if (this.minzoom && zoom < this.minzoom) return true;
        if (this.maxzoom && zoom >= this.maxzoom) return true;
        return this.visibility === 'none';
    }

    updateTransitions(parameters: TransitionParameters) {
        this._paintValues.updateTransitions(parameters);
        this.paintVersion++;
        this._dirty = true;
    }

    hasTransition(): boolean {
        return this._paintValues.hasTransition();
    }

    // The `*_DEPENDENCY` flags of all set layout, paint and appearance values.
    getPropertyDependencies(): number {
        let deps = (this._layoutValues ? this._layoutValues.deps : 0) | (this._paintValues ? this._paintValues.deps : 0);
        for (const {unevaluatedLayout, unevaluatedPaint} of this.appearances) {
            if (unevaluatedLayout) deps |= unevaluatedLayout.deps | unevaluatedPaint.deps;
        }
        return deps;
    }

    // Re-evaluates the values that depend on parameters changed since the last recalculation, or all of them if the
    // layer is dirty; layers are shared across tiles on the worker, so tiles of the same zoom mostly reuse values.
    recalculate(parameters: EvaluationParameters, availableImages: ImageId[]) {
        const last = this._recalcParameters;
        let changedDeps: number | undefined;
        if (!this._dirty && last && parameters.worldview === last.worldview && parameters.activeFloors === last.activeFloors) {
            changedDeps = this.getPropertyDependencies() & (
                (parameters.zoom !== last.zoom || parameters.pitch !== last.pitch ? ZOOM_DEPENDENCY : 0)
                | (parameters.brightness !== last.brightness ? LIGHT_DEPENDENCY : 0)
                | (availableImages !== this._recalcImages ? IMAGE_DEPENDENCY : 0));
            if (!changedDeps) return;
        }
        this._recalcParameters = parameters;
        this._recalcImages = availableImages;
        if (this._layoutValues) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            (this as any).layout = this._layoutValues.possiblyEvaluate(parameters, undefined, availableImages, this.iconImageUseTheme, changedDeps);
        }

        if (this.hasTransition()) this.paintVersion++;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        (this as any).paint = this._paintValues.possiblyEvaluate(parameters, undefined, availableImages, undefined, changedDeps);
        this._dirty = this.hasTransition();
        this._finalizeEvaluated(parameters, availableImages);
    }

    // Fixes up the freshly evaluated `layout` and `paint`; overridden by layers that derive or rewrite values
    _finalizeEvaluated(_parameters: EvaluationParameters, _availableImages: ImageId[]) {}

    serialize(): LayerSpecification {
        assert(this.type !== 'custom', 'Custom layers cannot be serialized');

        const output = {
            'id': this.id,
            'type': this.type,
            'slot': this.slot,
            'source': this.source,
            'source-layer': this.sourceLayer,
            'metadata': this.metadata,
            'minzoom': this.minzoom,
            'maxzoom': this.maxzoom,
            'filter': this.filter,
            'layout': this._layoutValues && this._layoutValues.serialize(),
            'paint': this._paintValues && this._paintValues.serialize()
        } as LayerSpecification;

        if (this.appearances.length !== 0) {
            output['appearances'] = this.appearances.map(a => a.serialize());
        }

        return filterObject(output, (value, key) => {
            return value !== undefined &&
                !(key === 'layout' && !Object.keys(value).length) &&
                !(key === 'paint' && !Object.keys(value).length);
        });
    }

    // Determines if the layer is 3D based on whether terrain is enabled.
    // If 'terrainEnabled' parameter is not provided, then the function
    // should return true if the layer is potentially 3D.
    is3D(terrainEnabled?: boolean): boolean {
        return false;
    }

    hasElevation(): boolean {
        return false;
    }

    // Conservative predicate used to preload a lazy module on both threads before the
    // first relevant tile parses/deserializes. Reads the *raw* layout declaration via
    // `_layoutValues` because this runs on the worker before layers have been
    // recalculated, and on the main thread during recalculate. Data-driven expression
    // declarations return true conservatively — the worst case is an unnecessary preload.
    // Subclasses override this to indicate which module(s) they require.
    mayUse(_type: RuntimeModuleType): boolean {
        return false;
    }

    // Worker-side module preload hook. Default no-op; subclasses return the relevant
    // `prepare*()` promise (from `modules/hd_worker` or `modules/standard_worker`) when
    // their layer type needs async module loading before bucket creation.
    // Main-side preload stays at call sites to avoid pulling main-only chunks into the
    // worker bundle.
    prepare(): Promise<void> {
        return Promise.resolve();
    }

    isSky(): boolean {
        return false;
    }

    isTileClipped(): boolean {
        return false;
    }

    /**
     * True if this layer draws see-through 3D geometry in two passes: first the depth buffer,
     * then the color of the closest surface only. The painter groups the depth passes of adjacent
     * layers of this type, because each pixel must blend one time only.
     * An opaque layer must return false. It must draw even if a nearer layer is see-through,
     * or a hole becomes visible.
     *
     * @private
     */
    hasBlended3DDepthPrepass(): boolean {
        return false;
    }

    createBucket?(parameters: BucketParameters<this>): Bucket;

    hasOffscreenPass(): boolean {
        return false;
    }

    hasShadowPass(): boolean {
        return false;
    }

    canCastShadows(): boolean {
        return false;
    }

    hasLightBeamPass(): boolean {
        return false;
    }

    cutoffRange(): number {
        return 0.0;
    }

    tileCoverLift(): number {
        return 0.0;
    }

    resize() {
        // noop
    }

    _clear() {
        // noop
    }

    isStateDependent(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        for (const property in (this as any).paint._values) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const value = (this as any).paint.get(property);
            if (!(value instanceof PossiblyEvaluatedPropertyValue) || !supportsPropertyExpression(value.property.specification)) {
                continue;
            }

            if ((value.value.kind === 'source' || value.value.kind === 'composite') &&
                value.value.isStateDependent) {
                return true;
            }
        }

        // Check if any appearance condition depends on feature state
        for (const appearance of this.appearances) {
            if (!isStateConstant(appearance.condition.expression)) {
                return true;
            }
        }

        return false;
    }

    compileFilter(options?: ConfigOptions | null) {
        if (!this._filterCompiled) {
            this._featureFilter = featureFilter(this.filter, this.scope, options);
            this._filterCompiled = true;
        }
    }

    invalidateCompiledFilter() {
        this._filterCompiled = false;
    }

    dynamicFilter(): FilterExpression | null | undefined {
        return this._featureFilter.dynamicFilter;
    }

    dynamicFilterNeedsFeature(): boolean {
        return this._featureFilter.needFeature;
    }

    dynamicFilterNeedsGeometry(): boolean {
        return this._featureFilter.needGeometry;
    }

    getLayerRenderingStats(): LayerRenderingStats | null | undefined {
        return this._stats;
    }

    resetLayerRenderingStats(painter: Painter) {
        if (this._stats) {
            if (painter.renderPass === 'shadow') {
                this._stats.numRenderedVerticesInShadowPass = 0;
            } else {
                this._stats.numRenderedVerticesInTransparentPass = 0;
            }
        }
    }

    getAppearances() {
        return this.appearances;
    }

    queryRenderedFeatures(
        queryGeometry: QueryGeometry,
        sourceCache: SourceCache,
        transform: Transform
    ) : QueryResult { return {}; }

    queryRadius(_bucket: Bucket): number | undefined { return undefined; }

    queryIntersectsFeature(
        _queryGeometry: TilespaceQueryGeometry,
        _feature: VectorTileFeature,
        _featureState: FeatureState,
        _geometry: Array<Array<Point>>,
        _zoom: number,
        _transform: Transform,
        _pixelPosMatrix: Float32Array,
        _elevationHelper: DEMSampler | null | undefined,
        _layoutVertexArrayOffset: number,
        scope: string | undefined
    ): boolean | number | undefined { return undefined; }
}

/**
 * Reads a raw layout declaration and evaluates `predicate` against it. Returns `true`
 * if the declaration is an expression (conservative — we can't evaluate without
 * feature/zoom context), `false` if the declaration is missing, otherwise defers to
 * `predicate`. Shared helper for `mayUse('HD')` implementations on subclasses.
 *
 * @private
 */
export function rawLayoutMayUseHD(layer: StyleLayer, propName: string, predicate: (v: string) => boolean): boolean {
    const val = layer.getLayoutPropertyValue(propName)?.value;
    if (val === undefined) return false;
    if (typeof val !== 'string') return true;
    return predicate(val);
}

export default StyleLayer;
